import { useContext, useCallback } from 'react';
import _ from 'underscore';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { BudgetSuggestionTiers } from '../model/constants';

const formatCustomBudgetPerformanceMetrics = ({ performanceMetricsResponse }) => {
  const template = {};

  template.CustomBudgetPerformance = JSON.stringify(performanceMetricsResponse);

  return template;
};

const formatBudgetSuggestionResponse = ({ budgetResponse }) => {
  const template = {};

  _.each(budgetResponse, (value, tier) => {
    const { BudgetAmountMonthly, BudgetAmountDaily, BudgetDetails } = value;

    template[`${tier}BudgetAmountMonthly`] = BudgetAmountMonthly;
    template[`${tier}BudgetAmountDaily`] = BudgetAmountDaily;
    template[`${tier}BudgetPerformance`] = JSON.stringify(BudgetDetails);
  });

  return template;
};

const formatBudgetTemplate = ({
  campaign,
  budgetResponse,
  appliedBudget,
  budgetTier,
}) => {
  const { Id, Budget, BiddingScheme } = campaign;

  const formattedBudgetResponse = formatBudgetSuggestionResponse({ budgetResponse });

  let tier = BudgetSuggestionTiers[budgetTier];

  if (budgetTier === 'CustomBudget') {
    tier = 'CustomBudget';
  }

  return {
    CampaignID: Id,
    CurrentBudget: Budget,
    BiddingStrategy: BiddingScheme,
    ...formattedBudgetResponse,
    BudgetApplied: appliedBudget,
    BudgetTier: tier,
  };
};

const formatBudgetLandscapeTemplate = ({ campaign, budgetLandscapeResponse }) => {
  const { Id } = campaign;

  const template = formatBudgetSuggestionResponse({ budgetResponse: budgetLandscapeResponse });

  return {
    CampaignID: Id,
    ...template,
  };
};

export const useBudgetMetricsLogger = () => {
  const activity = useContext(ActivityContext);

  const logBudgetFetchMetrics = useCallback(({
    campaign,
    budgetResponse,
  }) => {
    if (activity) {
      activity.trace(`[BudgetSuggestionV2][BudgetSuggestion] - ${JSON.stringify(formatBudgetTemplate({ campaign, budgetResponse }))}`);
    }
  }, [activity]);

  const logCustomBudgetPerformanceMetrics = useCallback(({
    campaign,
    performanceMetricsResponse,
  }) => {
    if (activity) {
      activity.trace(`[BudgetSuggestionV2][PerformanceMetrics] - ${JSON.stringify(formatCustomBudgetPerformanceMetrics({ campaign, performanceMetricsResponse }))}`);
    }
  }, [activity]);

  const logBudgetLandscapeMetrics = useCallback(({
    campaign,
    budgetLandscapeResponse,
  }) => {
    if (activity) {
      activity.trace(`[BudgetSuggestionV2][BudgetLandscape] - ${JSON.stringify(formatBudgetLandscapeTemplate({ campaign, budgetLandscapeResponse }))}`);
    }
  }, [activity]);

  const saveBudgetSuggestionMetrics = useCallback(({
    campaign,
    budgetResponse,
    appliedBudget,
    budgetTier,
  }) => {
    if (budgetTier === null && activity) {
      activity.trace(`[BudgetSuggestionV2][BudgetApplied][ManualInput] - ${JSON.stringify(formatBudgetTemplate({
        campaign,
        budgetResponse,
        appliedBudget,
        budgetTier,
      }))}`);
    } else if (activity) {
      activity.trace(`[BudgetSuggestionV2][BudgetApplied] - ${JSON.stringify(formatBudgetTemplate({
        campaign,
        budgetResponse,
        appliedBudget,
        budgetTier,
      }))}`);
    }
  }, [activity]);

  return {
    logBudgetFetchMetrics,
    logCustomBudgetPerformanceMetrics,
    logBudgetLandscapeMetrics,
    saveBudgetSuggestionMetrics,
  };
};
